'use client'

import React from 'react'
import moment from 'moment'
import { $DCprimaryColor, $DCWarningColor, DcChart, colors } from 'pixel-base'

import { formatDateForApi } from 'pixel-base'
import { getDashboardComplaints } from 'utils/api-routes-analytics'
import {
  colorSquare,
  tooltipTrigger,
  axisPointerStyle,
  tooltipPosition,
  tooltipStyle,
} from './chartStyles'

interface ComplaintItem {
  name: string
  open: number
  closed: number
}

interface ComplaintData {
  date: string
  complaints: ComplaintItem[]
}

interface ComplaintsProps {
  filters: {
    start_date: Date
    end_date: Date
    data_type: string
  }
  height?: number | string
  setComponent: (_component: string) => void
  setShowDetail: (_showDetail: boolean) => void
  width?: string
}

interface ChartParam {
  axisValue: string
  value: number
  color: string
  seriesName: string
}

const ComplaintsChart: React.FC<ComplaintsProps> = ({
  filters,
  height = 350,
  setComponent,
  setShowDetail,
  width,
}: ComplaintsProps) => {
  const [data, setData] = React.useState<ComplaintData[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    setLoading(true)
    getDashboardComplaints({
      start_date: formatDateForApi(filters.start_date),
      end_date: formatDateForApi(filters.end_date),
      data_type: filters.data_type,
    }).subscribe((res: any) => {
      const response = res as { response: ComplaintData[] }
      setData(response?.response || [])
      setLoading(false)
    })
  }, [filters.start_date, filters.end_date, filters.data_type])

  const formatData = () => {
    if (!Array.isArray(data) || data.length === 0) {
      return {
        xAxis: [],
        series: [],
      }
    }

    const dates = data.map((item) => moment(item.date).format('DD Mmm'))
    const openData = data.map((item) => item.complaints[0]?.open || 0)
    const closedData = data.map((item) => item.complaints[0]?.closed || 0)

    return {
      xAxis: dates,
      stackName: 'complaints',
      series: [
        {
          name: 'Open',
          data: openData,
          itemStyle: { color: $DCprimaryColor },
        },
        {
          name: 'Closed',
          data: closedData,
          itemStyle: { color: $DCWarningColor },
        },
      ],
    }
  }

  const chartData = formatData()

  const chartOptions = {
    tooltip: {
      trigger: tooltipTrigger,
      axisPointer: axisPointerStyle,
      position: tooltipPosition,
      backgroundColor: tooltipStyle.backgroundColor,
      borderColor: tooltipStyle.borderColor,
      borderWidth: tooltipStyle.borderWidth,
      extraCssText: tooltipStyle.extraCssText,
      textStyle: tooltipStyle.textStyle,
      formatter: (params: ChartParam[]) => {
        if (!params || params.length === 0) return ''

        const date = params[0].axisValue
        // Start building the tooltip HTML
        let html = `
          <div style="margin-bottom: 8px;">
            <div style="font-size: 15px; font-weight: 600; margin-bottom: 4px;">${date}</div>
            <div style="height: 1px; background: ${colors.grayBorderLight}; margin: 8px 0;"></div>
          </div>
        `

        // Add each data series
        params.forEach((param: ChartParam) => {
          const value = param.value || 0
          const color = param.color
          const seriesName = param.seriesName

          html += `
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px; align-items: center;">
              <div style="display: flex; align-items: center;">
                ${colorSquare(color)}${seriesName}:
              </div>&nbsp;&nbsp;
              <div style="font-weight: 500;">${value}</div>
            </div>
          `
        })

        return html
      },
    },
    legend: {
      orient: 'horizontal',
      top: 25,
      left: 'center',
      padding: [0, 10, 10, 10],
      textStyle: {
        fontSize: 9,
        color: colors.grayTextDark,
      },
    },
    grid: {
      top: 60,
      bottom: 30,
      left: 50,
      right: 20,
    },
    yAxis: {
      name: 'Count',
      nameTextStyle: {
        padding: [0, 0, 0, 40],
      },
    },
  }

  return (
    <DcChart
      type="stackedBar"
      data={chartData}
      height={height}
      options={chartOptions}
      isLoading={loading || false}
      title="Complaints"
      isTitleClickable={true}
      onClickTitle={() => {
        setComponent('complaintsChart')
        setShowDetail(true)
      }}
    />
  )
}

export default ComplaintsChart
